import { Focus } from '@studio/models/control/focus';
import type { Device } from '@studio/models/device';
import { useDocumentActive, useLatestFn, useLazyRef, useMount } from '@studio/react-utils/hooks';
import { useEffect } from 'react';
import { Choreographer } from './choreographer.js';
import * as Commands from './commands/index.js';
import { useListener as useListener_ } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import { useFormula } from '#~/hot-keys/hooks.js';
import { evaluate } from '#~/hot-keys/index.js';
import { raiseError } from '#~/notifications/index.js';

/**
 * Command listener.
 *
 * @param deviceId - Device ID.
 */
export const useListener = (deviceId: Device['id']): void => {
  const isDocumentActive = useDocumentActive();
  const hkFormula = useFormula();

  const handleFocus = useLatestFn((intent: Focus | false) => {
    const k: keyof typeof Commands =
      intent === Focus.Near ? 'near' : intent === Focus.Far ? 'far' : 'stop';

    Commands[k](deviceId).catch((error) => raiseError(error, { actionable: true }));
  });

  const choreographer = useLazyRef(() => new Choreographer(handleFocus)).current;

  // Handle keyup events.
  const handleKeyUp = useLatestFn((event: KeyboardEvent) => {
    const directive = evaluate(hkFormula, event);

    if (!directive) {
      return;
    }

    if (directive.command === Command.FOCUS_NEAR) {
      choreographer.remove(Focus.Near);
    } else if (directive.command === Command.FOCUS_FAR) {
      choreographer.remove(Focus.Far);
    }
  });

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    // Listen for keyup events.
    document.addEventListener('keyup', handleKeyUp, { signal });

    return () => {
      // Cleanup event listeners.
      abortController.abort();

      // Stop all focus on unmount.
      choreographer.stop();
    };
  });

  // Stop all focus when the document is not active.
  useEffect(() => {
    if (!isDocumentActive) {
      choreographer.stop();
    }
  }, [choreographer, isDocumentActive]);

  useListener_(({ command }) => {
    if (command === Command.FOCUS_FAR) {
      choreographer.add(Focus.Far);
    } else if (command === Command.FOCUS_NEAR) {
      choreographer.add(Focus.Near);
    } else if (command === Command.FOCUS_MODE) {
      Commands.mode(deviceId).catch((error) => raiseError(error, { actionable: true }));
    } else if (command === Command.FOCUS_ONE_PUSH) {
      Commands.push(deviceId).catch((error) => raiseError(error, { actionable: true }));
    } else if (command === Command.FOCUS_STOP) {
      choreographer.stop();
    }
  });
};
